import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ComputerIcon from '@mui/icons-material/Computer';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { getSecurityClientDetailsHref } from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import type { WazuhClientItem } from 'src/types/mindtime/wazuhDashboard';
import type { Column, TableData } from 'src/types/table';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import { EdrDataCard, EdrStatusPill, SubscriptionUnavailableMessage } from '../shared/edrPrimitives';
import { SecurityDashboardToolbar } from '../shared/securityDashboardToolbar';
import { useClientsData, type ClientsSortParams } from './useClientsData';

const cardSx = {
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  boxShadow: 'none',
  backgroundColor: '#FFFFFF',
} as const;

const COLOR_GREEN = '#16A34A';
const COLOR_RED = '#DC2626';

const STATUS_STYLES: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: '#0D9488', bg: '#CCFBF1' },
};

const RISK_STYLES: Record<string, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEE2E2' },
  high: { label: 'High', color: '#EA580C', bg: '#FFEDD5' },
  medium: { label: 'Medium', color: '#CA8A04', bg: '#FEF9C3' },
  low: { label: 'Low', color: '#16A34A', bg: '#DCFCE7' },
  unknown: { label: 'Unknown', color: '#64748B', bg: '#F1F5F9' },
};

const Pill: FC<{ label: string; color: string; bg?: string }> = ({ label, color, bg }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1,
      py: 0.25,
      borderRadius: '4px',
      fontSize: 11,
      fontWeight: 600,
      color,
      backgroundColor: bg ?? 'transparent',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const StatCard: FC<{
  title: string;
  value: string | number;
  subtitle?: ReactNode;
  icon: ReactNode;
  iconBg: string;
  loading?: boolean;
}> = ({ title, value, subtitle, icon, iconBg, loading = false }) => (
  <Card sx={cardSx} style={{ padding: '16px 18px', height: '100%' }}>
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>{title}</Typography>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: iconBg,
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography style={{ fontSize: 28, fontWeight: 700, color: '#1E293B', lineHeight: 1.1 }}>
      {loading ? '—' : value}
    </Typography>
    {subtitle ? <Box mt={0.5}>{subtitle}</Box> : null}
  </Card>
);

const SecurityScoreBar: FC<{ score: number | null }> = ({ score }) => {
  if (score === null || !Number.isFinite(score)) {
    return (
      <Typography style={{ fontSize: 12, color: '#94A3B8' }}>—</Typography>
    );
  }

  const clamped = Math.min(100, Math.max(0, score));
  const color = clamped >= 70 ? COLOR_GREEN : clamped >= 40 ? '#EA580C' : COLOR_RED;
  const track = clamped >= 70 ? '#DCFCE7' : clamped >= 40 ? '#FFEDD5' : '#FEE2E2';

  return (
    <Box display="flex" alignItems="center" style={{ gap: 10, minWidth: 120 }}>
      <Box
        style={{
          flex: 1,
          minWidth: 60,
          height: 6,
          borderRadius: 3,
          backgroundColor: track,
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            width: `${clamped}%`,
            height: '100%',
            backgroundColor: color,
            borderRadius: 3,
          }}
        />
      </Box>
      <Typography style={{ fontSize: 12, fontWeight: 600, color, minWidth: 32 }}>
        {Math.round(clamped)}%
      </Typography>
    </Box>
  );
};

const formatStatus = (status?: string) => {
  const key = (status ?? '').trim().toLowerCase();
  return STATUS_STYLES[key] ?? {
    label: status?.trim() || '—',
    color: '#64748B',
    bg: '#F1F5F9',
  };
};

const formatRisk = (risk?: string) => {
  const key = (risk ?? '').trim().toLowerCase();
  return RISK_STYLES[key] ?? {
    label: risk?.trim() || '—',
    color: '#64748B',
    bg: '#F1F5F9',
  };
};

const SecurityClients: FC = () => {
  const navigate = useNavigate();
  const { scope } = useWazuhScope();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortParams, setSortParams] = useState<ClientsSortParams | null>(null);
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'clients',
  );
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const showUnavailable = permissionsResolved && !canSummary && !canList;

  useEffect(() => {
    setPage(0);
  }, [scope.organizationId, scope.startDate, scope.endDate]);

  const { loading, summaryLoading, summary, items } = useClientsData(
    page,
    rowsPerPage,
    '',
    sortParams
  );

  const rows = items?.items ?? [];
  const totalRows = items?.totalRecords ?? 0;

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'clientName',
        label: 'Client Name',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
            {(row as unknown as WazuhClientItem).clientName || '—'}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        render: (row) => {
          const { label, color, bg } = formatStatus((row as unknown as WazuhClientItem).status);
          return <Pill label={label} color={color} bg={bg} />;
        },
      },
      {
        id: 'risk',
        label: 'Risk',
        render: (row) => {
          const { label, color, bg } = formatRisk((row as unknown as WazuhClientItem).risk);
          return <Pill label={label} color={color} bg={bg} />;
        },
      },
      {
        id: 'endpointCount',
        label: 'Endpoints',
        align: 'right',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'right' }}>
            {(row as unknown as WazuhClientItem).endpointCount ?? 0}
          </Typography>
        ),
      },
      {
        id: 'securityScore',
        label: 'Security Score',
        sortable: true,
        render: (row) => (
          <SecurityScoreBar score={(row as unknown as WazuhClientItem).securityScore} />
        ),
      },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: row.clientId,
      })) as unknown as TableData[],
    [rows]
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Clients')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <Typography
          component="h1"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: '#1E293B',
            lineHeight: 1.2,
          }}
        >
          Clients
        </Typography>

        {!showUnavailable ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{ gap: 12 }}
          >
            <SecurityDashboardToolbar showSearch={false} layout="inline" />
          </Box>
        ) : null}

        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.clients" />
        ) : null}

        {canSummary ? (
        <Box
          display="grid"
          style={{
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 16,
          }}
        >
          <StatCard
            title="Total Clients"
            value={summary?.totalClients ?? 0}
            loading={summaryLoading}
            icon={<ComputerIcon style={{ fontSize: 18, color: '#3182CE' }} />}
            iconBg="#DBEAFE"
          />
          <StatCard
            title="Protected Endpoints"
            value={summary?.protectedEndpoints?.total ?? 0}
            loading={summaryLoading}
            subtitle={
              summaryLoading ? undefined : (
                <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6 }}>
                  <EdrStatusPill
                    tone="medium"
                    label={`${summary?.protectedEndpoints?.warning ?? 0} Warning`}
                  />
                  <EdrStatusPill
                    tone="critical"
                    label={`${summary?.protectedEndpoints?.critical ?? 0} Critical`}
                  />
                </Box>
              )
            }
            icon={<CheckCircleOutlineIcon style={{ fontSize: 18, color: COLOR_GREEN }} />}
            iconBg="#DCFCE7"
          />
          <StatCard
            title="Clients Requiring Attention"
            value={summary?.clientsRequiringAttention ?? 0}
            loading={summaryLoading}
            icon={<ReportProblemOutlinedIcon style={{ fontSize: 18, color: COLOR_RED }} />}
            iconBg="#FEE2E2"
          />
        </Box>
        ) : null}

        {canList ? (
        <EdrDataCard>
          <Table
            columns={columns}
            data={tableRows}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRows}
            onPageChange={setPage}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setRowsPerPage(nextRowsPerPage);
              setPage(0);
            }}
            onSort={(columnId, direction) => {
              setSortParams(direction ? { column: columnId, sort: direction } : null);
              setPage(0);
            }}
            onRowClick={(row) => {
              const client = row as unknown as WazuhClientItem;
              const clientId = client.clientId?.trim();
              if (!clientId) return;
              navigate(getSecurityClientDetailsHref(clientId), {
                state: { clientName: client.clientName?.trim() || clientId },
              });
            }}
            showPagination
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}
      </Box>
    </>
  );
};

export default SecurityClients;
